/**
 * Permissions Service
 *
 * Fetch API implementation of the Angular permissions services.
 * Migrated from: assets/js/permissions.js
 */

import { fetchSessionJson, getSessionToken } from './SessionRequest';
import { toRequiredNumber } from './Utilities';

const API_SERVER = process.env.API_SERVER || '';
// Source of truth for permission constants is the licenses database.
export const PermissionConsts = {
  // User based permissions
  class_own_create: 'class_own_create',
  academy_access: 'academy_access',
  class_any_create: 'class_any_create',
  add_experience: 'add_experience',
  add_multi_class_experience: 'add_multi_class_experience',
  student_facing_feedback: 'student_facing_feedback',
  ui_curriculum_hide_student_facing_feedback: 'ui_curriculum_hide_student_facing_feedback',
  ui_navbar_hide_curriculum_reports_button: 'ui_navbar_hide_curriculum_reports_button',
  ui_class_show_licensing_assignment: 'ui_class_show_licensing_assignment',
  open_all_gates_on_activation: 'open_all_gates_on_activation',
  ui_show_admin_interface: 'ui_show_admin_interface',
  ui_show_toggle_teacher_notes: 'ui_show_toggle_teacher_notes',
  ui_disable_rostering: 'ui_disable_rostering',
  ui_analytics_review: 'ui_analytics_review',
  ui_tia_proctor: 'ui_tia_proctor',
  ui_whiteboard: 'ui_whiteboard',
  ui_skip_elements: 'ui_skip_elements',

  // Class based permissions
  view_class_reports: 'view_class_reports',
  ui_view_class_pack_ispire: 'ui_view_class_pack_ispire',
  ui_view_class_pack_ww: 'ui_view_class_pack_ww',
  ui_view_class_pack_spire: 'ui_view_class_pack_spire',
  ui_campus_rostering: 'ui_campus_rostering',
  ui_assign_student_status: 'ui_assign_student_status',
  ui_assign_district_id: 'ui_assign_district_id',
  ui_license_pack: 'ui_license_pack',
  ui_disable_class_rostering: 'ui_disable_class_rostering',

  // Curriculum based permissions
  ui_curriculum_hide_reports_placeholder: 'ui_curriculum_hide_reports_placeholder',
  ui_curriculum_show_dashboard_responses: 'ui_curriculum_show_dashboard_responses',
  integration_curriculum_google_drive: 'integration_curriculum_google_drive',
  ui_curriculum_reactivate_experiences: 'ui_curriculum_reactivate_experiences',
  ui_streaming_video: 'ui_streaming_video',

  // Subscription based permissions
  subscription_admin: 'subscription_admin',
  ispire_rosters_and_licenses: 'ispire_rosters_and_licenses',
  view_district_schools_subscriptions: 'view_district_schools_subscriptions',
  edit_district_schools_subscriptions: 'edit_district_schools_subscriptions',
  import_students_to_classes: 'import_students_to_classes',
  ui_curriculum_show_experience_card_standards: 'ui_curriculum_show_experience_card_standards',

  // School based permissions
  school_admin: 'school_admin',

  // District based permissions
  district_admin: 'district_admin',
} as const;

export type PermissionConstKey = keyof typeof PermissionConsts;
export type PermissionConstValue = (typeof PermissionConsts)[PermissionConstKey];

export interface District {
  district_id: number;
  [key: string]: unknown;
}

export interface School {
  school_id: number;
  [key: string]: unknown;
}

const cache = {
  userPermissions: new Map<number, string[]>(),
  experiencePermissions: new Map<number, string[]>(),
  userClassPermissions: new Map<string, string[]>(),
  userSubscriptionPermissions: new Map<string, string[]>(),
  adminDistricts: new Map<number, District[]>(),
  userDistrictPermissions: new Map<string, string[]>(),
  adminSchools: new Map<string, School[]>(),
  userSchoolPermissions: new Map<string, string[]>(),
};
let userPermissionsCacheVersion = 0;
let userPermissionsSessionToken: string | null = null;

/**
 * Fetches user permissions.
 * Permissions stay cached for the login session. Callers must not clear them before
 * ordinary requests; users sign in again to pick up changes. The next check clears
 * cached permissions when the session token changes or disappears.
 * @param userId - The user ID to fetch permissions for
 * @returns Promise resolving to an array of permission strings
 */
export async function getUserPermissions(userId: number): Promise<string[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const cacheKey = normalizedUserId;
  const sessionToken = getSessionToken();
  if (sessionToken !== userPermissionsSessionToken || !sessionToken) {
    clearUserPermissionsCache();
    userPermissionsSessionToken = sessionToken;
  }

  if (cache.userPermissions.has(cacheKey)) {
    return cache.userPermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}`;

  const requestCacheVersion = userPermissionsCacheVersion;
  const data = await fetchSessionJson<string[]>(url, 'Failed to fetch user permissions');
  // An old session's response must not refill the cache, even before the next permission check.
  if (sessionToken && getSessionToken() === sessionToken && requestCacheVersion === userPermissionsCacheVersion) {
    cache.userPermissions.set(cacheKey, data);
  }
  return data;
}

/**
 * Checks if a permission exists in a permissions array.
 * @param permissions - Array of permission strings
 * @param permission - The permission to check for
 * @returns True if the permission exists
 */
export function hasPermission(permissions: string[], permission: string): boolean {
  return (permissions || []).includes(permission);
}

/**
 * Fetches experience permissions.
 * @param experienceId - The experience ID to fetch permissions for
 * @returns Promise resolving to an array of permission strings
 */
export async function getExperiencePermissions(experienceId: number): Promise<string[]> {
  const normalizedExperienceId = toRequiredNumber(experienceId, 'experienceId');
  if (normalizedExperienceId <= 0) {
    throw new Error('experienceId must be a positive number');
  }

  const cacheKey = normalizedExperienceId;

  if (cache.experiencePermissions.has(cacheKey)) {
    return cache.experiencePermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/experiences/${encodeURIComponent(normalizedExperienceId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch experience permissions: ${response.status} ${response.statusText}`);
  }

  const data: string[] = await response.json();
  cache.experiencePermissions.set(cacheKey, data);
  return data;
}

/**
 * Fetches user class permissions.
 * @param userId - The user ID
 * @param classId - The class ID
 * @returns Promise resolving to an array of permission strings
 */
export async function getUserClassPermissions(
  userId: number,
  classId: number
): Promise<string[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const normalizedClassId = toRequiredNumber(classId, 'classId');
  if (normalizedClassId <= 0) {
    throw new Error('classId must be a positive number');
  }

  const cacheKey = `${normalizedUserId}:${normalizedClassId}`;

  if (cache.userClassPermissions.has(cacheKey)) {
    return cache.userClassPermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/classes/${encodeURIComponent(normalizedClassId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user class permissions: ${response.status} ${response.statusText}`);
  }

  const data: string[] = await response.json();
  cache.userClassPermissions.set(cacheKey, data);
  return data;
}

/**
 * Fetches user subscription permissions.
 * @param userId - The user ID
 * @param subscriptionId - The subscription ID
 * @returns Promise resolving to an array of permission strings
 */
export async function getUserSubscriptionPermissions(
  userId: number,
  subscriptionId: number
): Promise<string[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const normalizedSubscriptionId = toRequiredNumber(subscriptionId, 'subscriptionId');
  if (normalizedSubscriptionId <= 0) {
    throw new Error('subscriptionId must be a positive number');
  }

  const cacheKey = `${normalizedUserId}:${normalizedSubscriptionId}`;

  if (cache.userSubscriptionPermissions.has(cacheKey)) {
    return cache.userSubscriptionPermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/subscriptions/${encodeURIComponent(normalizedSubscriptionId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user subscription permissions: ${response.status} ${response.statusText}`);
  }

  const data: string[] = await response.json();
  cache.userSubscriptionPermissions.set(cacheKey, data);
  return data;
}

/**
 * Fetches districts where the user is an admin.
 * @param userId - The user ID
 * @returns Promise resolving to an array of District objects
 */
export async function getAdminDistricts(userId: number): Promise<District[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const cacheKey = normalizedUserId;

  if (cache.adminDistricts.has(cacheKey)) {
    return cache.adminDistricts.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/districts/admin`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch admin districts: ${response.status} ${response.statusText}`);
  }

  const data: District[] = await response.json();
  cache.adminDistricts.set(cacheKey, data);
  return data;
}

/**
 * Fetches user district permissions.
 * @param userId - The user ID
 * @param districtId - The district ID
 * @returns Promise resolving to an array of permission strings
 */
export async function getUserDistrictPermissions(
  userId: number,
  districtId: number
): Promise<string[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const normalizedDistrictId = toRequiredNumber(districtId, 'districtId');
  if (normalizedDistrictId <= 0) {
    throw new Error('districtId must be a positive number');
  }

  const cacheKey = `${normalizedUserId}:${normalizedDistrictId}`;
  if (cache.userDistrictPermissions.has(cacheKey)) {
    return cache.userDistrictPermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/district/${encodeURIComponent(normalizedDistrictId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user district permissions: ${response.status} ${response.statusText}`);
  }

  const data: string[] = await response.json();
  cache.userDistrictPermissions.set(cacheKey, data);
  return data;
}

/**
 * Fetches schools where the user is an admin.
 * @param userId - The user ID
 * @returns Promise resolving to an array of School objects
 */
export async function getAdminSchools(userId: number): Promise<School[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const cacheKey = String(normalizedUserId);

  if (cache.adminSchools.has(cacheKey)) {
    return cache.adminSchools.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/schools/admin`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch admin schools: ${response.status} ${response.statusText}`);
  }

  const data: School[] = await response.json();
  cache.adminSchools.set(cacheKey, data);
  return data;
}

/**
 * Fetches user school permissions.
 * @param userId - The user ID
 * @param schoolId - The school ID
 * @returns Promise resolving to an array of permission strings
 */
export async function getUserSchoolPermissions(
  userId: number,
  schoolId: number
): Promise<string[]> {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  const normalizedSchoolId = toRequiredNumber(schoolId, 'schoolId');
  if (normalizedSchoolId <= 0) {
    throw new Error('schoolId must be a positive number');
  }

  const cacheKey = `${normalizedUserId}:${normalizedSchoolId}`;

  if (cache.userSchoolPermissions.has(cacheKey)) {
    return cache.userSchoolPermissions.get(cacheKey)!;
  }

  const url = `${API_SERVER}/permissions/users/${encodeURIComponent(normalizedUserId)}/school/${encodeURIComponent(normalizedSchoolId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user school permissions: ${response.status} ${response.statusText}`);
  }

  const data: string[] = await response.json();
  cache.userSchoolPermissions.set(cacheKey, data);
  return data;
}

/**
 * Clears all cached permissions data.
 */
export function clearAllCaches(): void {
  userPermissionsCacheVersion++;
  cache.userPermissions.clear();
  cache.experiencePermissions.clear();
  cache.userClassPermissions.clear();
  cache.userSubscriptionPermissions.clear();
  cache.adminDistricts.clear();
  cache.userDistrictPermissions.clear();
  cache.adminSchools.clear();
  cache.userSchoolPermissions.clear();
}

/**
 * Clears cached user permissions.
 * Session changes are handled automatically; do not call before ordinary permission checks.
 * @param userId - Optional user ID to clear specific cache entry
 */
export function clearUserPermissionsCache(userId?: number): void {
  userPermissionsCacheVersion++;
  if (userId !== undefined) {
    const normalizedUserId = toRequiredNumber(userId, 'userId');
    if (normalizedUserId <= 0) {
      throw new Error('userId must be a positive number');
    }
    cache.userPermissions.delete(normalizedUserId);
  } else {
    cache.userPermissions.clear();
  }
}

/**
 * Clears cached experience permissions.
 * @param experienceId - Optional experience ID to clear specific cache entry
 */
export function clearExperiencePermissionsCache(experienceId?: number): void {
  if (experienceId !== undefined) {
    const normalizedExperienceId = toRequiredNumber(experienceId, 'experienceId');
    if (normalizedExperienceId <= 0) {
      throw new Error('experienceId must be a positive number');
    }
    cache.experiencePermissions.delete(normalizedExperienceId);
  } else {
    cache.experiencePermissions.clear();
  }
}

const PermissionsService = {
  PermissionConsts,
  hasPermission,
  getUserPermissions,
  getExperiencePermissions,
  getUserClassPermissions,
  getUserSubscriptionPermissions,
  getAdminDistricts,
  getUserDistrictPermissions,
  getAdminSchools,
  getUserSchoolPermissions,
  clearAllCaches,
  clearUserPermissionsCache,
  clearExperiencePermissionsCache,
};

export default PermissionsService;
